import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import productLogo from "../assets/images/country-seal.jpg";
import { getAccessToken, clearAuth } from "../api/apiClient";
import { certificateService } from "../services/api/certificate.service";
import type { CertificateApplication } from "../types/certificate.types";
import { UserStatusId } from "../types/user.types";
import {
  showErrorToast,
  showSuccessToast,
  showDeleteConfirm,
} from "../utils/toast";

/**
 * Simplified self-service portal for Applicant-role users.
 * Lists the applicant's own certificate applications and lets them start a new one.
 * (Backend own-data scoping restricts the list to the current applicant.)
 */
const Portal = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState<CertificateApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"marriage" | "divorce">("marriage");
  const firstName = localStorage.getItem("twdc_first_name") || "Applicant";

  const isDivorceApp = (app: CertificateApplication) =>
    (app.category || "").toLowerCase().includes("divorce");

  const visibleApplications = applications.filter(
    (app) =>
      // Once a certificate is printed it drops off the self-service list.
      app.statusId !== UserStatusId.Printed &&
      (activeTab === "divorce" ? isDivorceApp(app) : !isDivorceApp(app)),
  );

  useEffect(() => {
    // Basic guard: must be authenticated.
    if (!getAccessToken()) {
      navigate("/");
      return;
    }
    const roleName = (localStorage.getItem("twdc_role_name") || "").toLowerCase();
    // Staff users shouldn't land here; send them to the dashboard.
    if (roleName && roleName !== "applicant") {
      navigate("/dashboard");
      return;
    }
    fetchApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const data = await certificateService.getMyApplications();
      setApplications(Array.isArray(data) ? data : []);
    } catch (err) {
      showErrorToast("Could not load your applications.");
      setApplications([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    clearAuth();
    navigate("/");
  };

  const formatDate = (d?: string) => {
    if (!d) return "—";
    try {
      return new Date(d).toLocaleDateString("en-ZA", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return d;
    }
  };

  // An application is still editable only before it has been submitted.
  const isEditable = (app: CertificateApplication) =>
    app.statusId === UserStatusId.Created ||
    app.statusId === UserStatusId.Queried;

  const openApplication = (app: CertificateApplication) => {
    if (!app.id) return;
    // Submitted applications open read-only on the summary page (no edits allowed).
    if (!isEditable(app)) {
      navigate(`/portal/summary/${app.id}`);
      return;
    }
    const isDivorce = (app.category || "").toLowerCase().includes("divorce");
    navigate(
      isDivorce
        ? `/portal/divorce/apply/${app.id}`
        : `/portal/marriages/apply/${app.id}`,
    );
  };

  // An application can only be deleted before it has been submitted.
  const canDelete = (app: CertificateApplication) => isEditable(app);

  // The certificate can be printed once the application is approved (or beyond).
  const printableStatuses = new Set<number>([
    UserStatusId.Approved,
    UserStatusId.Verified,
    UserStatusId.Attested,
    UserStatusId.Completed,
    UserStatusId.Printed,
  ]);
  const canPrint = (app: CertificateApplication) =>
    app.statusId !== undefined && printableStatuses.has(app.statusId);

  const printApplication = (app: CertificateApplication) => {
    if (!app.id) return;
    navigate(
      isDivorceApp(app)
        ? `/portal/divorce/print/${app.id}`
        : `/portal/marriages/print/${app.id}`,
    );
  };

  const handleDelete = async (app: CertificateApplication) => {
    if (!app.id || !canDelete(app)) return;
    const confirmed = await showDeleteConfirm(
      app.formNo || "this application",
      "application",
    );
    if (!confirmed) return;
    try {
      await certificateService.hardDeleteApplication(app.id);
      showSuccessToast("Application deleted.");
      setApplications((prev) => prev.filter((a) => a.id !== app.id));
    } catch (err) {
      showErrorToast(
        err instanceof Error ? err.message : "Could not delete the application.",
      );
    }
  };

  return (
    <div className="portal-body">
      <header className="portal-header">
        <div className="portal-brand">
          <img src={productLogo} alt="Seal" />
          <div>
            <strong>Traditional Marriage &amp; Divorce Certificates</strong>
            <span>Self-Service Portal</span>
          </div>
        </div>
        <div className="portal-header-actions">
          <span className="portal-greeting">Hi, {firstName}</span>
          <button className="portal-btn portal-btn--ghost" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </header>

      <main className="portal-main">
        <section className="portal-start">
          <h2>Start a New Application</h2>
          <p>Register a new marriage or divorce certificate request.</p>
          <div className="portal-start-actions">
            <button
              className="portal-btn portal-btn--primary"
              onClick={() => navigate("/portal/marriages/new")}
            >
              💍 New Marriage Certificate
            </button>
            <button
              className="portal-btn portal-btn--primary"
              onClick={() => navigate("/portal/divorce/new")}
            >
              📄 New Divorce Certificate
            </button>
          </div>
        </section>

        <section className="portal-list">
          <h2>My Applications</h2>

          <div className="portal-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "marriage"}
              className={`portal-tab${activeTab === "marriage" ? " portal-tab--active" : ""}`}
              onClick={() => setActiveTab("marriage")}
            >
              💍 Marriage
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "divorce"}
              className={`portal-tab${activeTab === "divorce" ? " portal-tab--active" : ""}`}
              onClick={() => setActiveTab("divorce")}
            >
              📄 Divorce
            </button>
          </div>

          {loading ? (
            <p className="portal-muted">Loading…</p>
          ) : visibleApplications.length === 0 ? (
            <p className="portal-muted">
              You have no {activeTab} applications yet. Start one above.
            </p>
          ) : (
            <div className="portal-table-wrap">
              <table className="portal-table">
                <thead>
                  <tr>
                    <th>Form No</th>
                    <th>County</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleApplications.map((app) => (
                    <tr key={app.id}>
                      <td>{app.formNo || "—"}</td>
                      <td>{app.countyName || "—"}</td>
                      <td>{formatDate(app.dateOfWedding)}</td>
                      <td>
                        <span className="portal-status">
                          {app.status?.name || app.statusName || "—"}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: 8 }}>
                          <button
                            className="portal-btn portal-btn--sm"
                            onClick={() => openApplication(app)}
                          >
                            Open
                          </button>
                          {canPrint(app) && (
                            <button
                              className="portal-btn portal-btn--sm portal-btn--primary"
                              onClick={() => printApplication(app)}
                              title="Print your certificate"
                            >
                              🖨️ Print
                            </button>
                          )}
                          {canDelete(app) && (
                            <button
                              className="portal-btn portal-btn--sm portal-btn--danger"
                              onClick={() => handleDelete(app)}
                              title="Permanently delete this application"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default Portal;
